import { useMemo } from 'react'
import { useAuth } from '@/contexts/AuthContext'

export interface UserRole {
  isPrimaryKitchen: boolean
  isPrimaryFOH: boolean
  isPrimaryTraining: boolean
  isShiftLead: boolean
  isDirector: boolean
  isLeader: boolean
  isTeamMember: boolean
  isTrainer: boolean
  positionLevel: number
  primaryDepartment: string
  suggestedQuickActions: string[]
  suggestedStatCards: string[]
}

/**
 * Smart role detection hook that determines user's primary responsibilities
 * and suggests appropriate dashboard content.
 *
 * Uses positionLevel from database for smart permission detection:
 * - Level 1: Team Member
 * - Level 2: Trainer (can conduct assessments)
 * - Level 3+: Leader (shift lead, can manage team)
 * - Level 4+: Director (full management access)
 */
export function useRoleDetection(): UserRole {
  const { user } = useAuth()

  return useMemo(() => {
    if (!user) {
      return {
        isPrimaryKitchen: false,
        isPrimaryFOH: false,
        isPrimaryTraining: false,
        isShiftLead: false,
        isDirector: false,
        isLeader: false,
        isTeamMember: true,
        isTrainer: false,
        positionLevel: 1,
        primaryDepartment: 'Unknown',
        suggestedQuickActions: [],
        suggestedStatCards: []
      }
    }

    const departments = user.departments || []
    const position = (user.position || 'Team Member').toLowerCase()

    // Use positionLevel from database (smart detection)
    // Falls back to name-based detection for backwards compatibility
    const positionLevel = user.positionLevel || 1

    // Smart role detection using level from database
    // Level 4+ = Director, Level 3+ = Leader, Level 2 = Trainer, Level 1 = Team Member
    const isDirector = positionLevel >= 4 ||
      position.includes('director') ||
      position === 'owner operator' ||
      position.includes('administrator')

    const isLeader = positionLevel >= 3 ||
      position.includes('leader') ||
      isDirector

    const isTrainer = positionLevel >= 2 ||
      position === 'trainer' ||
      isLeader

    const isTeamMember = positionLevel === 1 &&
      !isLeader && !isDirector && !isTrainer

    const isShiftLead = isLeader || isDirector

    // Detect primary department based on user's departments array
    const isPrimaryKitchen = Boolean(user.departmentCategories?.includes('BOH')) ||
                            departments.includes('Kitchen') ||
                            departments.includes('Prep') ||
                            departments.includes('Food Truck')
    
    const isPrimaryFOH = departments.includes('Front Counter') || 
                        departments.includes('Drive Thru')
    
    // Training director detection - could be based on custom logic
    // For now, we'll use Director position as proxy
    const isPrimaryTraining = isDirector

    // Determine primary department
    let primaryDepartment = 'General'
    if (isPrimaryKitchen && !isPrimaryFOH) {
      primaryDepartment = 'Kitchen'
    } else if (isPrimaryFOH && !isPrimaryKitchen) {
      primaryDepartment = 'FOH'
    } else if (departments.includes('Everything')) {
      primaryDepartment = 'All Departments'
    } else if (departments.length > 0) {
      primaryDepartment = departments[0]
    }

    // Suggest quick actions based on role and department
    const suggestedQuickActions: string[] = []
    
    if (isDirector) {
      // Directors see everything
      suggestedQuickActions.push('team-overview', 'evaluations', 'analytics', 'documentation', 'training', 'leadership')
    } else if (isLeader) {
      // Leaders see their area
      if (isPrimaryKitchen) {
        suggestedQuickActions.push('kitchen', 'waste-tracker', 'equipment', 'food-safety')
      } else if (isPrimaryFOH) {
        suggestedQuickActions.push('foh-tasks', 'setup-sheets', 'team-overview')
      }
      suggestedQuickActions.push('evaluations', 'training', 'documentation')
    } else {
      // Team members see their tasks
      suggestedQuickActions.push('my-training', 'my-evaluations', 'my-profile')
      if (isPrimaryKitchen) {
        suggestedQuickActions.push('kitchen')
      }
      if (isPrimaryFOH) {
        suggestedQuickActions.push('foh-tasks')
      }
    }

    // Suggest stat cards based on role
    const suggestedStatCards: string[] = []
    
    if (isDirector) {
      // Directors get overview of everything
      suggestedStatCards.push('team-size', 'pending-evaluations', 'training-progress', 'documentation-count')
    } else if (isLeader && isPrimaryKitchen) {
      // Kitchen leaders
      suggestedStatCards.push('waste-today', 'equipment-alerts', 'food-safety', 'kitchen-team')
    } else if (isLeader && isPrimaryFOH) {
      // FOH leaders
      suggestedStatCards.push('foh-completion', 'setup-status', 'team-scheduled', 'pending-evals')
    } else if (isLeader && isPrimaryTraining) {
      // Training leaders
      suggestedStatCards.push('active-trainees', 'pending-assessments', 'new-hires', 'completion-rate')
    } else if (isLeader) {
      // General leaders
      suggestedStatCards.push('team-size', 'pending-evaluations', 'training-progress', 'recent-documentation')
    }

    return {
      isPrimaryKitchen,
      isPrimaryFOH,
      isPrimaryTraining,
      isShiftLead,
      isDirector,
      isLeader,
      isTeamMember,
      isTrainer,
      positionLevel,
      primaryDepartment,
      suggestedQuickActions,
      suggestedStatCards
    }
  }, [user])
}

